import React from 'react';
import { Link } from 'react-router-dom';
import { apiCatalog } from '../data/apiCatalog';
import { BRAND } from '../data/brand';
import { PageHeader } from '../components/PageHeader';
const FLOW = [{
  title: 'Fetch in the browser',
  text: 'Every live figure is requested by your browser straight from a public, keyless API. There is no server of ours in between.'
}, {
  title: 'Cache per visit',
  text: 'Each response is cached in memory for the visit, so moving between pages doesn’t fetch the same data twice.'
}, {
  title: 'Fall back, never blank',
  text: 'If an API fails or rate-limits, an embedded, dated snapshot takes over and the badge switches from Live to Snapshot.'
}, {
  title: 'Model on top',
  text: 'Our estimates, like endpoints and agents per capita, are computed from those fields with published formulas and labeled grade C.'
}];
const DATASETS = [{
  name: 'Countries',
  to: '/countries',
  fields: 'population, internet %, users, median age, mobile/100, endpoint & agent estimates (active scenario)'
}, {
  name: 'Models',
  to: '/models',
  fields: 'model, lab, country, release, params, context, license, modality, source'
}, {
  name: 'Builders',
  to: '/builders',
  fields: 'org, country, type, focus, product, status, source'
}];
export function Api() {
  const embed = `<iframe src="${BRAND.url}/embed?theme=dark&scenario=device" width="480" height="150" style="border:0" title="${BRAND.name} live counters"></iframe>`;
  return <>
      <PageHeader title="API & data" lede={`How ${BRAND.domain} is assembled, and every public API it reads, so you can build on the same stack.`} data-id="element-869" data-mp="m4ug5g6il8ps" />

      <div className="mx-auto max-w-[1400px] px-5 pt-10 md:px-8" data-id="element-870" data-mp="w1pd3un5311y">
        <section aria-labelledby="arch-title" data-id="element-871" data-mp="10zh66rzist7q">
          <h2 id="arch-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-872" data-mp="10z8uh3f4vty4">
            Data architecture
          </h2>
          <ol className="mt-6 grid gap-x-10 gap-y-8 md:grid-cols-4" data-id="element-873" data-mp="2xudo4q60yssa">
            {FLOW.map((f, i) => <li key={f.title} className="border-t border-line pt-4" data-id="element-874" data-mp="2uqfxx1u9c2dd" data-mpi={i}>
                <p className="font-mono text-[12px] text-machine" data-id="element-875" data-mp="2uqo9mqen91mz">{i + 1}</p>
                <h3 className="mt-1 text-[15px] font-medium text-ink" data-id="element-876" data-mp="2psmrc7p1l6ck">{f.title}</h3>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted" data-id="element-877" data-mp="34ovmax1wgxj6">{f.text}</p>
              </li>)}
          </ol>
        </section>

        <section aria-labelledby="apis-title" className="mt-16" data-id="element-878" data-mp="2zrvhepgse0tn">
          <h2 id="apis-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-879" data-mp="3dnphcizl1fum">
            Public APIs we consume
          </h2>
          <p className="mt-2 text-[14px] text-muted" data-id="element-880" data-mp="38octpw0w04qz">All keyless and callable from a browser.</p>
          <div className="mt-5 overflow-x-auto rounded-lg border border-line" data-id="element-881" data-mp="3ngxwbpk1dfkg">
            <table className="w-full min-w-[1080px] border-collapse text-[13px]" data-id="element-882" data-mp="3igsc2ug3ivce">
              <thead className="bg-surface text-left text-[12px] text-muted" data-id="element-883" data-mp="b6x3aajo6ll">
                <tr data-id="element-884" data-mp="3seziwkgo7x0o">
                  {['API', 'Endpoint', 'Used for', 'Limits', 'If it fails', 'Pages'].map(h => <th key={h} scope="col" className="whitespace-nowrap border-b border-line px-3 py-2.5 font-normal" data-id="element-885" data-mp="exm6lojr4cma">
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-886" data-mp="5yqxo4pboofa">
                {apiCatalog.map(a => <tr key={a.name} className="border-b border-line/60 align-top last:border-b-0" data-id="element-887" data-mp="atxwoz7nd4xv">
                    <th scope="row" className="whitespace-nowrap px-3 py-3 text-left font-normal" data-id="element-888" data-mp="3saftygirauvi">
                      <a href={a.docs} target="_blank" rel="noopener noreferrer" className="text-ink hover:text-machine" data-id="element-889" data-mp="yw947t0saxe">
                        {a.name}
                      </a>
                    </th>
                    <td className="max-w-[360px] break-all px-3 py-3 font-mono text-[11.5px] text-ink/80" data-id="element-890" data-mp="3ndfpga1k206i">{a.endpoint}</td>
                    <td className="px-3 py-3 text-muted" data-id="element-891" data-mp="39fhab2fetzpy">{a.usedFor}</td>
                    <td className="px-3 py-3 text-muted" data-id="element-892" data-mp="3ebok56axfvd6">{a.limits}</td>
                    <td className="px-3 py-3 text-muted" data-id="element-893" data-mp="kj6okzojnmah">{a.fallback}</td>
                    <td className="px-3 py-3" data-id="element-894" data-mp="giwkxto857ys">
                      <span className="flex flex-col gap-0.5" data-id="element-895" data-mp="gj69rdxj992w">
                        {a.pages.map(p => <Link key={p.to} to={p.to} className="whitespace-nowrap text-muted underline decoration-line underline-offset-2 hover:text-machine" data-id="element-896" data-mp="bq3hra8em7ot">
                            {p.label}
                          </Link>)}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </section>

        <section aria-labelledby="ours-title" className="mt-16 grid gap-12 lg:grid-cols-2" data-id="element-897" data-mp="3p0asvp9kpm9h">
          <div data-id="element-898" data-mp="3k1pt1qy8h4lk">
            <h2 id="ours-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-899" data-mp="1n6tem2qxtn5r">
              Our datasets
            </h2>
            <p className="mt-2 text-[14px] text-muted" data-id="element-900" data-mp="1i91mp8qam11k">These downloads are our API until a real one exists. Each table has CSV and JSON buttons, and files are generated in your browser from the data you’re looking at, dated in the filename.</p>
            <ul className="mt-5 divide-y divide-line border-y border-line" data-id="element-901" data-mp="1uoj5rzu890qe">
              {DATASETS.map(d => <li key={d.name} className="py-3.5" data-id="element-902" data-mp="24gfgqbhewlc1">
                  <Link to={d.to} className="text-[15px] text-ink hover:text-machine" data-id="element-903" data-mp="24f45xrpjudbf">
                    {d.name} →
                  </Link>
                  <p className="mt-0.5 font-mono text-[11.5px] text-faint" data-id="element-904" data-mp="1brqe280pk7y6">{d.fields}</p>
                </li>)}
            </ul>
          </div>
          <div data-id="element-905" data-mp="1gp9qr3hrcco0">
            <h2 className="font-serif text-2xl text-ink md:text-3xl" data-id="element-906" data-mp="1gpjlf4dqz801">Embed the counters</h2>
            <p className="mt-2 text-[14px] text-muted" data-id="element-907" data-mp="1ktra51k7gny9">
              A transparent widget with the live counters, the ratio and attribution. Use <span className="font-mono text-ink" data-id="element-908" data-mp="2w7l8o4mzbcq8">theme=light|dark</span> and <span className="font-mono text-ink" data-id="element-909" data-mp="2w5s20pcd8si8">scenario=verifiable|device|ambient</span>.
            </p>
            <pre className="mt-4 overflow-x-auto rounded-md border border-line bg-surface p-3 font-mono text-[11.5px] leading-relaxed text-ink" data-id="element-910" data-mp="x10goqto3zsb">{embed}</pre>
            <Link to="/embed?theme=dark&scenario=device" className="mt-3 inline-block text-[13px] text-muted underline decoration-line underline-offset-2 hover:text-machine" data-id="element-911" data-mp="163rtatya51lj">
              Preview the widget →
            </Link>
          </div>
        </section>
      </div>
    </>;
}